import { useEffect, useState, useCallback, useMemo } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import VehicleCard from "@/components/VehicleCard";
import VehicleDetailSheet from "@/components/VehicleDetailSheet";
import AddVehicleWizard from "@/components/dialogs/AddVehicleWizard";
import AddEnergyDialog from "@/components/dialogs/AddEnergyDialog";
import SettingsDialog from "@/components/dialogs/SettingsDialog";
import FeedbackDialog from "@/components/dialogs/FeedbackDialog";
import { Button } from "@/components/ui/button";
import { VehiclesAPI, StatsAPI, SettingsAPI, EnergyAPI, MaintenanceAPI } from "@/lib/api";
import MaintenanceStatusCard from "@/components/MaintenanceStatusCard";
import { symbolFor } from "@/lib/currency";
import { resolveTz } from "@/lib/tz";
import logger from "@/lib/logger";
import useConfirm from "@/lib/useConfirm";
import MobileFab from "@/components/MobileFab";
import Footer from "@/components/Footer";
import WhatsNewDialog from "@/components/dialogs/WhatsNewDialog";

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [statsByVehicle, setStatsByVehicle] = useState({});
  const [unit, setUnit] = useState(() => localStorage.getItem("mileage_unit") || "km");
  const [showAdd, setShowAdd] = useState(false);
  const [activeVehicleId, setActiveVehicleId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showEnergy, setShowEnergy] = useState(false);
  const [energyDefaultVehicleId, setEnergyDefaultVehicleId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    localStorage.setItem("mileage_unit", unit);
  }, [unit]);

  // In-app confirm dialog — replaces `window.confirm` which is blocked
  // inside Emergent's preview iframe on modern Chromium.
  const { confirm, ConfirmDialog } = useConfirm();

  const refresh = useCallback(async () => {
    try {
      const vs = await VehiclesAPI.list();
      setVehicles(vs);
      const entries = await Promise.all(
        vs.map((v) =>
          StatsAPI.get({ vehicle_id: v.id })
            .then((s) => [v.id, s])
            .catch((err) => {
              logger.warn("vehicle-stats-failed", err?.message || String(err), {
                vehicleId: v.id,
              });
              return [v.id, null];
            })
        )
      );
      const map = {};
      entries.forEach(([id, s]) => (map[id] = s));
      setStatsByVehicle(map);
    } catch (e) {
      logger.error("vehicles-refresh-failed", e?.message || String(e));
      toast.error("Failed to load vehicles");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    SettingsAPI.get().then(setSettings).catch(() => {});
  }, []);

  // Aggregate maintenance summary (across all vehicles the user owns).
  const [maintSummary, setMaintSummary] = useState(null);
  useEffect(() => {
    MaintenanceAPI.summary().then(setMaintSummary).catch(() => setMaintSummary(null));
  }, [vehicles.length]);

  const currencySymbol = settings?.currency_symbol || symbolFor(settings?.currency || "USD");
  const tz = useMemo(() => resolveTz(settings), [settings]);

  const handleAddEnergy = async (data) => {
    try {
      await EnergyAPI.create(data);
      toast.success("Energy entry saved");
    } catch {
      toast.error("Failed to save energy entry");
      throw new Error("save failed");
    }
  };

  const handleSaveSettings = async (data) => {
    try {
      const s = await SettingsAPI.update(data);
      setSettings(s);
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save settings");
    }
  };

  const handleCreate = async (data) => {
    try {
      await VehiclesAPI.create(data);
      toast.success("Vehicle added");
      refresh();
    } catch {
      toast.error("Failed to add vehicle");
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      await VehiclesAPI.update(id, data);
      toast.success("Vehicle updated");
      refresh();
    } catch {
      toast.error("Update failed");
    }
  };

  const handleDelete = async (id) => {
    // `window.confirm()` is blocked inside Emergent's preview iframe on
    // recent Chromium builds — replaced with an in-app AlertDialog so this
    // flow works in preview AND production. See /app/frontend/src/lib/useConfirm.js
    const ok = await confirm({
      title: "Delete vehicle?",
      description:
        "This permanently removes the vehicle and all of its logged trips and energy events. This can't be undone.",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      destructive: true,
    });
    if (!ok) return;
    try {
      await VehiclesAPI.remove(id);
      toast.success("Vehicle deleted");
      setActiveVehicleId(null);
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleArchive = async (id, archived) => {
    try {
      await VehiclesAPI.update(id, { archived });
      toast.success(archived ? "Vehicle archived" : "Vehicle restored");
      refresh();
    } catch {
      toast.error("Update failed");
    }
  };

  const active = vehicles.find((v) => v.id === activeVehicleId);
  const activeStats = active ? statsByVehicle[active.id] : null;

  const activeVehicles = vehicles.filter((v) => !v.archived);
  const archivedVehicles = vehicles.filter((v) => v.archived);

  return (
    <div className="min-h-screen bg-[var(--bg-deep)] text-[var(--text-primary)]">
      <Header
        vehicles={vehicles}
        showFilters={false}
        onOpenSettings={() => setShowSettings(true)}
        onOpenFeedback={() => setShowFeedback(true)}
      />

      <main className="px-6 lg:px-10 py-8 pb-24 max-w-[1600px] mx-auto" data-testid="vehicles-main">
        <div className="flex items-end justify-between mb-8 fade-up">
          <div>
            <div className="label-tiny mb-2">Garage</div>
            <h1 className="font-display text-3xl md:text-5xl font-light tracking-tighter">
              Vehicles
            </h1>
            <p className="text-sm text-[var(--text-muted)] mt-2">
              {activeVehicles.length} active · {archivedVehicles.length} archived
            </p>
          </div>
         {/*  Hiding the Add Vehicle button section on Mobile - as using Floating Action Button (FAB)  */}
          <Button
            onClick={() => setShowAdd(true)}
            className="hidden md:inline-flex bg-[#E82127] text-white hover:bg-[#FF2A30]"
            style={{ borderRadius: 2 }}
            data-testid="add-vehicle-btn"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={2.25} />
            Add Vehicle
          </Button>
        </div>

        {/* Maintenance banner intentionally hidden per user request — the
            per-vehicle Maintenance accordion inside VehicleDetailSheet is the
            canonical surface. Keeping the fetch + import in place so we can
            re-enable it later without another migration. */}
        {false && vehicles.length > 0 && (
          <div className="mb-6 fade-up" style={{ animationDelay: "0.04s" }}>
            <MaintenanceStatusCard summary={maintSummary} unit={unit} />
          </div>
        )}

        {vehicles.length === 0 ? (
          <div className="bg-[var(--bg-canvas)] border border-dashed border-[var(--border-hover)] p-16 flex flex-col items-center justify-center text-center" style={{ borderRadius: 4 }}>
            <div className="label-tiny mb-2">Empty Garage</div>
            <p className="text-[var(--text-muted)] text-sm mb-6">Add your first vehicle to start tracking mileage.</p>
            <Button
              onClick={() => setShowAdd(true)}
              className="bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)]"
              style={{ borderRadius: 2 }}
              data-testid="empty-add-vehicle-btn"
            >
              <Plus className="w-4 h-4 mr-2" strokeWidth={2.25} />
              Add Vehicle
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 fade-up" style={{ animationDelay: "0.05s" }}>
              {activeVehicles.map((v) => (
                <VehicleCard
                  key={v.id}
                  vehicle={v}
                  stats={statsByVehicle[v.id]}
                  unit={unit}
                  onClick={() => setActiveVehicleId(v.id)}
                />
              ))}
            </div>

            {archivedVehicles.length > 0 && (
              <>
                <div className="mt-12 mb-4 flex items-center gap-3">
                  <span className="label-tiny">Archived</span>
                  <div className="flex-1 h-px bg-[var(--bg-hover)]" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-60">
                  {archivedVehicles.map((v) => (
                    <VehicleCard
                      key={v.id}
                      vehicle={v}
                      stats={statsByVehicle[v.id]}
                      unit={unit}
                      onClick={() => setActiveVehicleId(v.id)}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
        <Footer />
      </main>

      <AddVehicleWizard
        open={showAdd}
        onOpenChange={setShowAdd}
        unit={unit}
        onCreate={handleCreate}
      />

      <VehicleDetailSheet
        open={!!activeVehicleId}
        onOpenChange={(o) => !o && setActiveVehicleId(null)}
        vehicle={active}
        unit={unit}
        stats={activeStats}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onArchive={handleArchive}
        currencySymbol={currencySymbol}
        onAddEnergy={(vid) => {
          setEnergyDefaultVehicleId(vid);
          setShowEnergy(true);
        }}
      />

      <AddEnergyDialog
        open={showEnergy}
        onOpenChange={setShowEnergy}
        vehicles={activeVehicles}
        defaultVehicleId={energyDefaultVehicleId || activeVehicleId}
        currencySymbol={currencySymbol}
        tz={tz}
        chargingRates={settings?.charging_rates || null}
        onSubmit={async (data) => {
          await handleAddEnergy(data);
          // Refresh detail sheet's energy list by reopening — set active id refresh
          if (activeVehicleId) {
            setActiveVehicleId(null);
            setTimeout(() => setActiveVehicleId(activeVehicleId), 50);
          }
        }}
      />
      <SettingsDialog
        open={showSettings}
        onOpenChange={setShowSettings}
        settings={settings}
        onSave={handleSaveSettings}
        unit={unit}
        onUnitChange={setUnit}
      />
      <FeedbackDialog open={showFeedback} onOpenChange={setShowFeedback} />
      <ConfirmDialog />
      <WhatsNewDialog />

      {/* Mobile-only FAB — Vehicles page exposes Add Vehicle (same wizard as desktop) */}
      <MobileFab
        testid="vehicles-fab"
        actions={[
          {
            key: "add-vehicle",
            label: "Add Vehicle",
            icon: Plus,
            testid: "vehicles-fab-add-vehicle",
            onClick: () => setShowAdd(true),
          },
        ]}
      />
    </div>
  );
}
